import { useQuery } from '@apollo/client'
import { ChevronRight } from '@mui/icons-material'
import {
    Alert,
    Box,
    Card,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import { type FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { recruiterRouteConfig } from 'recruiter/routes'
import { formatRelativeTime } from 'shared/utils'
import {
    BotAuditFeedbackFilter,
    BotAuditRange,
    BotLogLevel,
} from '__generated__/graphql'
import { BotAuditNeedsAttention, BotAuditStats } from './BotAudit.gql'
import { FeedbackPill, type Feedback } from './FeedbackPill'
import { StatCard } from './StatCard'
import { VerdictBar } from './VerdictBar'
import { VerdictPill, type Verdict } from './VerdictPill'

const RANGE_LABEL: Record<BotAuditRange, string> = {
    [BotAuditRange.Last24h]: 'Last 24h',
    [BotAuditRange.Last7d]: 'Last 7d',
    [BotAuditRange.Last30d]: 'Last 30d',
}

// The set of verdicts that qualify as "needs attention". Kept as a typed
// const so renaming the Verdict union (or eventually adding a backend
// BotVerdict enum, see issue #515) flags the right call sites.
const ATTENTION_VERDICTS: readonly Verdict[] = ['partial', 'no']

export const OverviewTab: FC = () => {
    const [range, setRange] = useState<BotAuditRange>(BotAuditRange.Last24h)
    const navigate = useNavigate()

    const {
        data: statsData,
        loading: statsLoading,
        error: statsError,
    } = useQuery(BotAuditStats, { variables: { range } })
    const {
        data: attentionData,
        loading: attentionLoading,
        error: attentionError,
    } = useQuery(BotAuditNeedsAttention)

    const stats = statsData?.botAuditStats

    const verdictBreakdown = stats?.verdictBreakdown
    const feedbackBreakdown = stats?.feedbackBreakdown
    const totalMessages = stats?.messages ?? 0
    const feedbackTotal =
        (feedbackBreakdown?.positive ?? 0) + (feedbackBreakdown?.negative ?? 0)
    const feedbackCoverage =
        totalMessages > 0
            ? Math.round((feedbackTotal / totalMessages) * 100)
            : 0

    // Needs-attention list intentionally ignores the range toggle - it always
    // shows the most recent flagged turns regardless of window, since the
    // point of the list is "what should I look at now".
    const allFlagged = [
        ...(attentionData?.partialOrNo?.rows ?? []),
        ...(attentionData?.negativeFeedback?.rows ?? []),
    ]
    const seen = new Map<string, (typeof allFlagged)[number]>()
    for (const row of allFlagged) {
        if (!seen.has(row.id)) seen.set(row.id, row)
    }
    const needsAttention = [...seen.values()]
        .sort(
            (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
        )
        .slice(0, 25)

    // Intentionally drops other URL params - clicking a flagged turn is a
    // jump-to-context action, not a filter refinement.
    const openConversation = (conversationId: string) => {
        navigate(
            `${recruiterRouteConfig.botAudit.path}?tab=conversations&conversationId=${conversationId}`
        )
    }

    return (
        <Stack spacing={3}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
            >
                <Typography variant="h6">Overview</Typography>
                <ToggleButtonGroup
                    size="small"
                    value={range}
                    exclusive
                    onChange={(_, v) => v && setRange(v)}
                    data-testid="bot-audit-range-toggle"
                >
                    {(Object.keys(RANGE_LABEL) as BotAuditRange[]).map((r) => (
                        <ToggleButton
                            key={r}
                            value={r}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                py: 0.25,
                                px: 1.5,
                            }}
                            data-testid={`bot-audit-range-${r}`}
                        >
                            {RANGE_LABEL[r]}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Stack>

            {statsError ? (
                <Alert severity="error">
                    Failed to load stats: {statsError.message}
                </Alert>
            ) : null}
            {attentionError ? (
                <Alert severity="error">
                    Failed to load needs-attention list:{' '}
                    {attentionError.message}
                </Alert>
            ) : null}

            {statsLoading && !stats ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <Grid
                    container
                    spacing={2}
                    sx={{
                        opacity: statsLoading ? 0.5 : 1,
                        transition: 'opacity 0.15s',
                    }}
                >
                    <Grid item xs={6} md={4} lg={2}>
                        <StatCard
                            label="Conversations"
                            value={stats?.conversations ?? 0}
                        />
                    </Grid>
                    <Grid item xs={6} md={4} lg={2}>
                        <StatCard label="Bot turns" value={totalMessages} />
                    </Grid>
                    <Grid item xs={12} md={4} lg={2}>
                        <StatCard
                            label="Judge verdicts"
                            value={
                                <VerdictBar
                                    yes={verdictBreakdown?.yes ?? 0}
                                    partial={verdictBreakdown?.partial ?? 0}
                                    no={verdictBreakdown?.no ?? 0}
                                />
                            }
                            sublabel={`${verdictBreakdown?.unjudged ?? 0} unjudged`}
                            to={`${recruiterRouteConfig.botAudit.path}?tab=conversations&verdicts=${ATTENTION_VERDICTS.join(',')}`}
                            testId="bot-audit-stat-verdicts"
                        />
                    </Grid>
                    <Grid item xs={12} md={4} lg={2}>
                        <StatCard
                            label="Feedback"
                            value={
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <FeedbackPill feedback="positive" />
                                    <Box>
                                        {feedbackBreakdown?.positive ?? 0}
                                    </Box>
                                    <FeedbackPill feedback="negative" />
                                    <Box>
                                        {feedbackBreakdown?.negative ?? 0}
                                    </Box>
                                </Stack>
                            }
                            sublabel={`${feedbackCoverage}% of turns rated`}
                            to={`${recruiterRouteConfig.botAudit.path}?tab=conversations&feedback=${BotAuditFeedbackFilter.Negative}`}
                            testId="bot-audit-stat-feedback"
                        />
                    </Grid>
                    <Grid item xs={6} md={4} lg={2}>
                        <StatCard
                            label="Latency"
                            value={
                                stats?.p50DurationMs != null
                                    ? `${stats.p50DurationMs}ms`
                                    : '—'
                            }
                            sublabel={
                                stats?.p95DurationMs != null
                                    ? `p95 ${stats.p95DurationMs}ms`
                                    : 'p95 —'
                            }
                        />
                    </Grid>
                    <Grid item xs={6} md={4} lg={2}>
                        <StatCard
                            label="Errors"
                            value={stats?.errorCount ?? 0}
                            sublabel="bot_logs level=error"
                            to={`${recruiterRouteConfig.botAudit.path}?tab=logs&levels=${BotLogLevel.Error}`}
                            testId="bot-audit-stat-errors"
                        />
                    </Grid>
                </Grid>
            )}

            <Stack spacing={1}>
                <Typography variant="subtitle1">Needs attention</Typography>
                <Typography variant="caption" color="text.secondary">
                    Recent bot turns with a partial / no verdict or a
                    thumbs-down. Click to open the thread.
                </Typography>
                <Card variant="outlined">
                    {attentionLoading && needsAttention.length === 0 ? (
                        <Box
                            sx={{
                                p: 3,
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress size={20} />
                        </Box>
                    ) : needsAttention.length === 0 ? (
                        <Box
                            sx={{
                                p: 3,
                                color: 'text.secondary',
                                textAlign: 'center',
                                fontSize: '0.875rem',
                            }}
                        >
                            Nothing flagged. Bot is doing fine.
                        </Box>
                    ) : (
                        <Stack
                            divider={
                                <Box
                                    sx={{
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                />
                            }
                        >
                            {needsAttention.map((row) => {
                                const userLabel = row.user
                                    ? `${row.user.firstName ?? ''} ${row.user.lastName ?? ''}`.trim() ||
                                      row.user.email ||
                                      row.slackUserId
                                    : row.slackUserId
                                const headline = row.precedingUserText?.trim()
                                return (
                                    <Stack
                                        key={row.id}
                                        direction="row"
                                        alignItems="flex-start"
                                        spacing={1.5}
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                                cursor: 'pointer',
                                            },
                                        }}
                                        onClick={() =>
                                            openConversation(row.conversationId)
                                        }
                                        data-testid={`bot-audit-needs-attention-row-${row.id}`}
                                    >
                                        <Stack
                                            spacing={0.5}
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    fontSize: '0.95rem',
                                                    fontWeight: 500,
                                                    color: 'text.primary',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {headline ? (
                                                    <>
                                                        &ldquo;{headline}&rdquo;
                                                    </>
                                                ) : (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        (no preceding user
                                                        message)
                                                    </Box>
                                                )}
                                            </Box>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    color: 'text.secondary',
                                                    minWidth: 0,
                                                }}
                                                useFlexGap
                                            >
                                                <VerdictPill
                                                    verdict={
                                                        row.judgeVerdict as Verdict
                                                    }
                                                />
                                                <FeedbackPill
                                                    feedback={
                                                        row.feedback as Feedback
                                                    }
                                                />
                                                <Box
                                                    component="span"
                                                    title={new Date(
                                                        row.createdAt
                                                    ).toLocaleString()}
                                                >
                                                    {formatRelativeTime(
                                                        new Date(row.createdAt)
                                                    )}
                                                </Box>
                                                <Box component="span">·</Box>
                                                <Box component="span">
                                                    {userLabel}
                                                </Box>
                                                {row.text ? (
                                                    <>
                                                        <Box component="span">
                                                            ·
                                                        </Box>
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                                whiteSpace:
                                                                    'nowrap',
                                                                flex: 1,
                                                                minWidth: 0,
                                                            }}
                                                        >
                                                            {row.text}
                                                        </Box>
                                                    </>
                                                ) : null}
                                            </Stack>
                                        </Stack>
                                        <IconButton
                                            size="small"
                                            sx={{ mt: -0.5 }}
                                        >
                                            <ChevronRight fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                )
                            })}
                        </Stack>
                    )}
                </Card>
            </Stack>
        </Stack>
    )
}
